import React from 'react';
import { motion } from 'framer-motion';
import TextType from './TextType';

export const AboutHero: React.FC = () => {
  return (
    <section 
      className="relative min-h-screen flex flex-col items-center justify-start overflow-hidden"
      style={{
        backgroundImage: `url(/Images/about_bg.webp)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {/* Overlay for better text readability */}
      <div className="absolute inset-0 bg-black/10" />
      
      {/* Top Section - Text with Leaf Icon */}
      <div className="container mx-auto px-4 relative z-10 pt-20 md:pt-32 mb-4 md:mb-8">
        <motion.div 
          className="text-center"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Leaf Icon */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="inline-flex items-center justify-center mb-4 md:mb-6"
          >
            <motion.div
              animate={{ 
                rotate: [0, 10, -10, 0],
                scale: [1, 1.1, 1]
              }}
              transition={{ 
                duration: 2,
                repeat: Infinity,
                repeatDelay: 1
              }}
            >
              <img src="/Images/about_leaves.webp" alt="Eco" className="w-12 h-12 md:w-20 md:h-20" />
            </motion.div>
          </motion.div>

          {/* Main Heading */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <TextType
              baseOpacity={0}
              enableBlur={true}
              baseRotation={5}
              blurStrength={10}
              size="lg"
              duration={1}
              textClassName="font-heading font-extrabold text-[24px] sm:text-[32px] md:text-[40px] leading-[1.3] text-center text-[#620131] whitespace-pre-line"
            >
              Precision. Innovation.
            </TextType>
            <TextType
              baseOpacity={0}
              enableBlur={true}
              baseRotation={5}
              blurStrength={10}
              size="lg"
              duration={1}
              textClassName="font-heading font-extrabold text-[24px] sm:text-[32px] md:text-[40px] leading-[1.3] text-center text-[#620131] whitespace-pre-line"
            >
              Premium mono-cartons.
            </TextType>
          </motion.div>
        </motion.div>
      </div>

      {/* Middle Section - Large PRINTAURA Background Text - Responsive */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-0">
        <motion.h2
          className="select-none"
          style={{
            fontFamily: 'Nanum Myeongjo',
            fontWeight: 800,
            fontSize: 'clamp(80px, 18vw, 303.41px)',
            lineHeight: '1',
            letterSpacing: '0%',
            background: 'linear-gradient(180deg, #FFC6D9 34.49%, rgba(255, 255, 255, 0) 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            opacity: 0.3
          }}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 0.3, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.5 }}
        >
          PrintAura
        </motion.h2>
      </div>

      {/* Bottom Section - Responsive Layout */}
      <div className="w-full px-4 relative z-10 pb-6 md:pb-8 mt-auto">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between gap-4">
            {/* Left Column - Empty on mobile, spacer on desktop */}
            <div className="hidden lg:flex flex-1 justify-start">
              {/* Empty space */}
            </div>

            {/* Center Column - Animated Product Box */}
            <motion.div
              className="flex justify-center flex-1 lg:flex-[2]"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.6 }}
            >
              <div className="relative w-full max-w-[350px] sm:max-w-[450px] md:max-w-[500px] lg:max-w-2xl mx-auto">
                <motion.div
                  animate={{ 
                    y: [0, -20, 0],
                    rotate: [0, 5, -5, 0]
                  }}
                  transition={{ 
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <img 
                    src="/Images/about_box.webp" 
                    alt="Premium Packaging" 
                    className="w-full drop-shadow-2xl"
                  />
                </motion.div>

                {/* Floating particles around the box - fewer on mobile */}
                {[...Array(10)].map((_, i) => (
                  <motion.div
                    key={i}
                    className={`absolute w-1.5 h-1.5 md:w-2 md:h-2 bg-gradient-to-r from-[#CA1167] to-[#F47749] rounded-full ${i > 5 ? 'hidden md:block' : ''}`}
                    style={{
                      top: `${Math.random() * 100}%`,
                      left: `${Math.random() * 100}%`,
                    }}
                    animate={{
                      y: [0, -30, 0],
                      opacity: [0.3, 1, 0.3],
                      scale: [1, 1.5, 1]
                    }}
                    transition={{
                      duration: 2 + i * 0.5,
                      repeat: Infinity,
                      delay: i * 0.3
                    }}
                  />
                ))}
              </div>
            </motion.div>

            {/* Right Column - Scroll Indicator - hidden on mobile */}
            <div className="hidden lg:flex flex-1 justify-end items-end">
              <motion.div
                className="z-10"
                animate={{ y: [0, 15, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <div className="w-10 h-16 border-[#CA1167] rounded-full flex justify-center" style={{ borderWidth: '3px' }}>
                  <motion.div
                    className="w-2.5 h-2.5 bg-[#CA1167] rounded-full mt-3"
                    animate={{ y: [0, 24, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  />
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

